/**
 * @file useLocaleFormat.ts
 * @description The React binding between the i18next runtime and
 * `@/utils/intlFormat` — how a component gets date and number formatting that
 * follows the signed-in user's `languagePreference`.
 * @module i18n
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  formatDate,
  formatNumber,
  formatTime,
  resolveFormattingLocale,
} from "@/utils/intlFormat";

import type { DateLike } from "@/utils/intlFormat";

/**
 * The active UI locale, as a `SupportedLocale` value (`de_DE`).
 *
 * Reads through `useTranslation` rather than the `i18n` singleton on purpose:
 * `useTranslation` SUBSCRIBES to i18next's `languageChanged` event, so a
 * component calling this re-renders when the language changes. Reading
 * `i18n.language` off the imported singleton returns the same string but
 * subscribes to nothing, and the screen would keep its old dates until some
 * unrelated state change happened to re-render it — the "requires a restart"
 * failure SE-7966's fourth scenario rules out.
 * @returns One of `SUPPORTED_UI_LOCALES`
 */
export function useAppLocale(): string {
  const { i18n } = useTranslation();
  return i18n.language;
}

/** Date and number formatters bound to the active UI locale. */
export interface LocaleFormat {
  /** The active locale as a BCP-47 tag (`de-DE`), for the rare direct `Intl` need. */
  readonly locale: string;
  /**
   * Formats a date in the active UI locale.
   * @param value - The moment to format
   * @param options - `Intl.DateTimeFormat` options
   * @returns The formatted date
   */
  readonly formatDate: (
    value: DateLike,
    options?: Intl.DateTimeFormatOptions
  ) => string;
  /**
   * Formats a time-of-day in the active UI locale.
   * @param value - The moment to format
   * @param options - `Intl.DateTimeFormat` options
   * @returns The formatted time
   */
  readonly formatTime: (
    value: DateLike,
    options?: Intl.DateTimeFormatOptions
  ) => string;
  /**
   * Formats a number in the active UI locale.
   * @param value - The number to format
   * @param options - `Intl.NumberFormat` options
   * @returns The formatted number
   */
  readonly formatNumber: (
    value: number,
    options?: Intl.NumberFormatOptions
  ) => string;
}

/**
 * Returns date and number formatters bound to the active UI locale.
 *
 * The sanctioned way for a component to format a date or a number. A call site
 * using this cannot pick up the device locale and cannot hardcode a locale
 * literal, which is the pair of mistakes SE-7966 removed.
 *
 * The returned object is memoized on the locale, so it is a stable dependency
 * for `useMemo`/`useCallback` and changes identity exactly once per language
 * change.
 * @returns Formatters bound to the active UI locale
 */
export function useLocaleFormat(): LocaleFormat {
  const locale = useAppLocale();
  return useMemo(
    () => ({
      locale: resolveFormattingLocale(locale),
      formatDate: (value: DateLike, options?: Intl.DateTimeFormatOptions) =>
        formatDate(value, locale, options),
      formatTime: (value: DateLike, options?: Intl.DateTimeFormatOptions) =>
        formatTime(value, locale, options),
      formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
        formatNumber(value, locale, options),
    }),
    [locale]
  );
}
